import type { ReactNode, Ref } from 'react';
import { useMemo } from 'react';

import { Alert, AlertDescription, AlertTitle } from 'design-system';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import useChartOptions from '@/features/charts/hooks/options/useChartOptions';
import useForwardCurveOptions from '@/features/charts/hooks/options/useForwardCurveOptions';
import useSeasonalOptions from '@/features/charts/hooks/options/useSeasonalOptions';
import useSpotOptions from '@/features/charts/hooks/options/useSpotOptions';
import RegularChart from '@/features/charts/RegularChart';
import type { ChartType, ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';

type BasicChartBlockType = {
  layout?: { fullWidth?: boolean; height?: number };
  loading: boolean;
  error: boolean;
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  chartType?: ChartType;
  buttonSection?: ReactNode;
  containerRef?: Ref<HTMLDivElement>;
  label?: string;
};
const BasicChartBlock = ({
  layout,
  loading,
  error,
  chartSeries,
  chartType = 'spot',
  buttonSection,
  containerRef,
  label = 'Market chart'
}: BasicChartBlockType) => {
  const { height } = layout || {};
  const { createOptions: createSeasonalOptions } = useSeasonalOptions(height);
  const { createOptions: createForwardCurveOptions } = useForwardCurveOptions(height);
  const { createOptions: createSpotOptions } = useSpotOptions(height);

  const optionsFactory = useMemo(() => {
    switch (chartType) {
      case 'forwardSimple':
        return createForwardCurveOptions;
      case 'seasonal':
        return createSeasonalOptions;
      default:
        return createSpotOptions;
    }
  }, [chartType, createForwardCurveOptions, createSeasonalOptions, createSpotOptions]);

  const { options: chartOptions } = useChartOptions(chartSeries, optionsFactory);
  const { hasPermission } = usePermission();

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <section
        aria-label={label}
        className='flex min-h-50 items-center justify-center'
      >
        <Alert className='max-w-md'>
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            You no longer have access. Please upgrade to access the full chart.
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  return (
    <section
      ref={containerRef}
      aria-label={label}
      className='chart-container'
      style={{ minHeight: 400, position: 'relative' }}
    >
      {buttonSection}
      <RegularChart
        showLegend
        chartSeries={chartSeries}
        chartType={chartType}
        error={error}
        height={height}
        loading={loading}
        options={chartOptions}
      />
    </section>
  );
};

export default BasicChartBlock;
